// Cache LOCAL (localStorage) do veredito "conteúdo saiu do catálogo" por item do
// rail "Continuar assistindo". NÃO é fonte da verdade — quem decide é sempre o
// backend (`availability === 'not_found'`, que o backend só emite com certeza).
// Este cache é uma MEMÓRIA DE UX que existe pra matar o FLICKER:
//
//   snapshot FRIO (cold-start) → enriched ainda não resolveu → tudo `unknown`
//   → o filtro MANTÉM o item (fail-open) → o card removido APARECE por um
//   instante; então o snapshot QUENTE chega com `not_found` → o card SOME.
//   Resultado: o item removido PISCA a cada boot.
//
// Persistindo o veredito, um item já confirmado `not_found` numa sessão já entra
// SUPRIMIDO no próximo cold-start (o filtro lê este cache), sem piscar.
//
// AUTO-CURÁVEL: o snapshot quente dizendo `available` (o provider REPÔS o item)
// LIMPA o veredito e o card volta sozinho — o IDB de progresso NUNCA é tocado
// (mesma filosofia do `continue-watching-availability`: reconcilia na LEITURA,
// não apaga o registro). Fail-open segue valendo pra item nunca-visto-removido
// (sem veredito).
//
// Escopo: por (provider, profile) via currentScope() — um veredito de um perfil
// nunca suprime no outro. TTL pra um veredito órfão (some do enriched pra
// sempre) não suprimir eternamente. LRU-bounded porque localStorage é pequeno e
// tem quota SEPARADA do IDB. INVARIANTE: NUNCA lança (storage indisponível /
// cheio / file:// restrito é engolido) — no pior caso o flicker volta, nada quebra.

import { currentScope } from '../scope'

const STORAGE_KEY = 'swipe:cw-unavailable:v1'

/** Vida de um veredito sem novo reforço. O caso comum reforça a cada sessão (a
 *  position removida segue no IDB → o enriched-batch segue perguntando → o
 *  backend segue respondendo `not_found` → o veredito é regravado). O TTL só
 *  atua no caso raro de o item sumir do enriched pra sempre. */
export const VERDICT_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 dias

/** Teto de vereditos guardados (todos os scopes juntos). localStorage é pequeno;
 *  mantemos os de expiração mais distante (regravados por último). */
export const VERDICT_MAX_ENTRIES = 200

interface Verdict {
  /** Scope completo (`p:CODE::pf:ID`) — isola por provider+profile. */
  scope: string
  /** Chave de reconciliação NÃO-scopada: `vod:<itemId>` ou `series:<seriesId>`
   *  (igual ao `continueAvailabilityKey`). É por ela que o filtro do rail casa. */
  key: string
  /** epoch ms de expiração (Date.now() + VERDICT_TTL_MS). */
  expiresAt: number
}

/** Acesso defensivo ao localStorage — pode nem existir (node/teste) ou lançar ao
 *  ser tocado (alguns WebViews de TV em file://). Espelha watch-progress-fallback. */
function store(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null
  }
}

/** Type guard — records vindos do JSON podem estar corrompidos/parciais. */
function isVerdict(v: unknown): v is Verdict {
  if (!v || typeof v !== 'object') return false
  const r = v as Record<string, unknown>
  return (
    typeof r['scope'] === 'string' &&
    typeof r['key'] === 'string' &&
    typeof r['expiresAt'] === 'number'
  )
}

/**
 * NÚCLEO PURO (testável sem storage): remove vereditos expirados e aplica o teto
 * LRU (mantém os de `expiresAt` mais distante = regravados por último). Ordem
 * não importa pro consumo (lookup por Set), mas o slice precisa de ordem estável.
 */
export function pruneAndBoundVerdicts(
  list: readonly Verdict[],
  now: number,
  max: number = VERDICT_MAX_ENTRIES,
): Verdict[] {
  return list
    .filter((v) => v.expiresAt > now)
    .sort((a, b) => b.expiresAt - a.expiresAt)
    .slice(0, max)
}

function readAll(): Verdict[] {
  const s = store()
  if (!s) return []
  try {
    const raw = s.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isVerdict)
  } catch {
    return []
  }
}

function writeAll(list: readonly Verdict[]): void {
  const s = store()
  if (!s) return
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(pruneAndBoundVerdicts(list, Date.now())))
  } catch {
    // localStorage cheio/indisponível — engole (o flicker volta, nada quebra).
  }
}

/**
 * Conjunto de chaves de reconciliação (`vod:ID` / `series:ID`) com veredito de
 * indisponibilidade VÁLIDO (não expirado) no scope ATUAL. É o que o filtro do
 * rail consome pra suprimir no cold-start. Vazio quando logged out ou sem
 * storage — fail-open natural.
 */
export function readUnavailableVerdictKeys(): Set<string> {
  const scope = currentScope()
  if (!scope) return new Set()
  const now = Date.now()
  const keys = new Set<string>()
  for (const v of readAll()) {
    if (v.scope === scope && v.expiresAt > now) keys.add(v.key)
  }
  return keys
}

/**
 * Grava/reforça o veredito de indisponibilidade dos `keys` no scope atual.
 * Retorna `true` só quando a MEMBRESIA mudou (chave nova entrou) — reforçar a
 * expiração de uma chave já presente retorna `false`, pra o caller não disparar
 * re-render à toa. No-op (retorna false) sem scope/storage.
 */
export function recordUnavailableVerdicts(keys: readonly string[]): boolean {
  const scope = currentScope()
  if (!scope || keys.length === 0) return false
  const list = readAll()
  const expiresAt = Date.now() + VERDICT_TTL_MS
  let membershipChanged = false
  for (const key of keys) {
    const idx = list.findIndex((v) => v.scope === scope && v.key === key)
    if (idx >= 0) {
      list[idx] = { scope, key, expiresAt } // reforça expiração, sem mudar membresia
    } else {
      list.push({ scope, key, expiresAt })
      membershipChanged = true
    }
  }
  writeAll(list)
  return membershipChanged
}

/**
 * Limpa o veredito dos `keys` no scope atual (o provider repôs o item → não
 * suprimir mais). Retorna `true` se removeu algo.
 */
export function clearUnavailableVerdicts(keys: readonly string[]): boolean {
  const scope = currentScope()
  if (!scope || keys.length === 0) return false
  const toClear = new Set(keys)
  const list = readAll()
  const next = list.filter((v) => !(v.scope === scope && toClear.has(v.key)))
  if (next.length === list.length) return false
  writeAll(next)
  return true
}

/**
 * Remoção AUTORITATIVA disparada pela ABERTURA da ficha: o usuário abriu o card
 * e o backend confirmou que o conteúdo saiu do catálogo (404 content_unavailable
 * / série sem episódios). Grava o veredito na hora pra o rail suprimir o item já
 * — não espera o próximo cold-start. `kind` casa com o `continueAvailabilityKey`.
 */
export function markContinueItemUnavailable(kind: 'vod' | 'series', itemId: string): void {
  recordUnavailableVerdicts([`${kind}:${itemId}`])
}

/** Zera o bucket inteiro (hard reset do local-db / "Limpar dados"). */
export function clearAllUnavailableVerdicts(): void {
  const s = store()
  if (!s) return
  try {
    s.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
